/**
 * LifeCraft4U Design System v1: "Night Glass".
 *
 * The dark hero base (stormy navy-plum + frosted glass) is the FINAL primary
 * theme. The Cleo-cream light palette stays inside the system as the tertiary
 * background family: some communities (morning yoga, Forever Fit) read better
 * on cream, so every component must work under both theme classes.
 *
 * Theming model: `.lc-dark` and `.lc-light` set CSS variables; components use
 * the `lc-*` Tailwind tokens and `.lc-glass` utilities, never hard-coded
 * per-theme colors. One library, two skins.
 *
 * Type: exactly TWO fonts. Tanker (display, single weight 400) and
 * Hanken Grotesk (body, UI, labels). Labels are tracked-uppercase Hanken,
 * not a third mono face.
 */

/* ---- background families ------------------------------------------- */
export const night = {
  900: '#130E22', // page gradient bottom, deepest surface
  800: '#191231', // primary page background (gradient top)
  700: '#170F2A', // gradient tail
  600: '#231A3E', // raised solid surface on dark (rare; prefer glass)
} as const

export const cream = {
  50: '#FAF6EE', // tertiary background: light-theme page top
  100: '#F4EEE2', // light-theme gradient middle
  200: '#EAE2D2', // light solid blocks, hairline fills
} as const

/* ---- ink (text) per theme ------------------------------------------ */
export const ink = {
  onDark: '#F2F1F6',
  onDarkMute: '#A7A3B4',
  onLight: '#17202B',
  onLightMute: '#5A6472',
} as const

/* ---- accents: theme-agnostic, identical on both skins --------------- */
export const accent = {
  lime: '#C8F04B', // THE action color: primary CTAs, key highlights
  limeInk: '#131118', // only text color allowed on lime
  leaf: '#547A00', // lime's readable sibling: small lime FOREGROUND accents flip to this on light skins (via --lc-accent / text-lc-accent)
  violet: '#7C3AED', // identity / brand blocks, hosts, "Cosmos" energy
  violetDeep: '#5B21B6', // dark violet support (blobs, hover on violet)
  coral: '#FF6B5C', // warmth / people / stickers
  cobalt: '#3B5BFE', // info chips, links, small functional accents
} as const

/* ---- candy tints: LIGHT-theme backdrop blobs only ------------------- */
export const candy = {
  pink: '#FFD3EF',
  lilac: '#D9D2FF',
  peach: '#FFE4C8',
  butter: '#E4F7B0',
} as const

/* ---- community hues: light-skin identities -------------------------- */
/*  Each light community gets its own hue family so pages read as       */
/*  distinct places under one brand. Hues tint BACKDROPS and small      */
/*  accents only; core UI (lime actions, ink, glass) never changes.     */
export type CommunityHue = {
  name: string
  bg: [string, string, string] // page gradient stops
  blobs: [string, string, string, string] // backdrop blobs
  deep: string // readable accent on the tinted cream (labels, icons)
}

export const communityHues: Record<string, CommunityHue> = {
  saffron: {
    name: 'Saffron', // Spirituality: temple marigold, morning ghee light
    bg: ['#FBF3E4', '#F6EAD2', '#F9F0DE'],
    blobs: ['#FFD9A0', '#F9C784', '#FFE9C2', '#F0DCB6'],
    deep: '#9C5C00',
  },
  rose: {
    name: 'Rose', // Rhythm: mehfil warmth, evening raag
    bg: ['#FBEFF1', '#F7E1E6', '#FAEDEB'],
    blobs: ['#FFC7D1', '#FFDCE9', '#F5B7C6', '#FFE0D5'],
    deep: '#AD3B55',
  },
  sage: {
    name: 'Sage', // reserved: Zen, Forever Fit mornings
    bg: ['#F2F6EC', '#E9F0DE', '#F1F5E7'],
    blobs: ['#CDE3B9', '#DFEDCA', '#BED9B0', '#EAF2D9'],
    deep: '#49682B',
  },
}

/* ---- dark-theme backdrop blob palette ------------------------------- */
export const nightBlobs = [accent.violet, accent.coral, accent.violetDeep, accent.lime] as const

/* ---- the two fonts -------------------------------------------------- */
export const type = {
  display: `'Tanker', 'Cabinet Grotesk', ui-sans-serif, system-ui, sans-serif`,
  body: `'Hanken Grotesk', ui-sans-serif, system-ui, -apple-system, sans-serif`,
} as const

/* Fluid display scale (Tanker). Body stays on the base rem scale. */
export const displayScale = {
  'display-xl': 'clamp(3.8rem, 0.2rem + 13vw, 9.5rem)', // hero statements
  'display-lg': 'clamp(3.2rem, 1.2rem + 8vw, 6rem)', // section breaks
  display: 'clamp(2.6rem, 1.4rem + 5vw, 4.4rem)', // section titles
  'display-sm': 'clamp(2rem, 1.3rem + 3vw, 3rem)', // card titles, sub-sections
} as const

/* ---- shape ----------------------------------------------------------- */
export const radius = {
  card: '28px', // glass cards, media
  panel: '22px', // nav bars, inner panels
  tile: '16px', // small tiles, icon wells
  pill: '9999px', // buttons, chips, labels
} as const

/* ---- text scale (Tailwind fontSize). Body >= 17px; display is fluid. -- */
export const fontSize = {
  xs: '0.8125rem',
  sm: '0.9375rem',
  base: '1.0625rem', // 17px
  lg: '1.1875rem',
  xl: '1.375rem',
  '2xl': '1.75rem',
  '3xl': displayScale['display-sm'],
  '4xl': displayScale.display,
  '5xl': displayScale['display-lg'],
  '6xl': displayScale['display-xl'],
  '7xl': 'clamp(4.2rem, -1rem + 18vw, 13rem)', // oversize break statements only
} as const

export const shadow = {
  card: '0 16px 44px rgba(19,14,34,0.16)',
  pop: '0 24px 70px rgba(19,14,34,0.32)',
} as const

export const space = { maxw: '1360px' } as const

/* ---- motion ----------------------------------------------------------- */
export const motion = {
  ease: 'cubic-bezier(0.22, 1, 0.36, 1)', // "brand": decisive out-expo feel
  pop: 'back.out(1.9)', // GSAP: bubbles, bursts, chips
  spring: { stiffness: 260, damping: 18 }, // framer: card/center-piece pops
  fast: 0.3, // hover micro-interactions (s)
  base: 0.5, // reveals (s)
  slow: 1.0, // scene / scroll beats (s)
} as const

/* ---- theme guidance -------------------------------------------------- */
export type LcTheme = 'dark' | 'light'

/** Which skin a surface defaults to. Communities may override to light. */
export const themeFor: Record<string, LcTheme> = {
  home: 'dark',
  cosmos: 'dark',
  zoin: 'dark',
  'the-stage': 'dark',
  rhythm: 'dark',
  spirituality: 'dark',
  zen: 'light',
  'forever-fit': 'light',
  lenscraft: 'light',
}
